import type { GetServerSideProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { BreadcrumbJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import React from 'react'
import Footer from 'src/components/Footer'
import LandingNav from 'src/components/LandingNav'
import WhatsAppButton from 'src/components/WhatsappButton'
import {
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  getPillarOgImage
} from 'src/constants/ogImages'
import {
  PILLAR_NS_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SearchPageCity
} from 'src/constants/searchPages'
import {
  DEFAULT_STRUCTURED_DATA,
  createStructuredData
} from 'src/constants/structuredData'
import { parseInitialCity } from 'src/hooks/useEffectiveCity'
import PersonalizedLanding from 'src/screens/PersonalizedLanding'
import { IPost, PostOrderByInput } from 'src/types/hygraph'
import { processHygraphRequestError } from 'src/utils/errors-hygraph'
import { PILLAR_BLOG_TOPIC, getPosts } from 'src/utils/hygraph'
import { loadTranslations } from 'src/utils/i18nServerProps'
import {
  buildOnDemandFaqEntry,
  resolveOnDemandLandingUrl
} from 'src/utils/onDemandFaq'
import { toOgLocale } from 'src/utils/seo'

const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'), {
  ssr: false
})

const VALID_PILLARS = new Set<string>(Object.values(PillarPage))

const urlF = (pillar: PillarPage) => `https://www.spotme.mx/${pillar}`

type IProps = {
  pillar: PillarPage
  searchName: string
  label: string
  locale: string
  firstBlogPost: IPost | null
  keywords: string[]
  seoTitle?: string
  seoDescription?: string
  priceRange?: { low: number; high: number }
  /** SSR-resolved `?city=<slug>` URL param. Null for organic traffic.
   *  When set, PersonalizedLanding renders the city-aware hero in SSR HTML
   *  → no client-side flash. */
  initialCity: SearchPageCity | null
}

const Page: NextPage<IProps> = ({
  pillar,
  searchName,
  label,
  locale,
  firstBlogPost,
  keywords,
  seoTitle,
  seoDescription,
  priceRange,
  initialCity
}) => {
  const title = seoTitle ?? `${searchName} en México | SpotMe`
  const description =
    seoDescription ??
    `✓ ${searchName} verificados. Compara precios, tamaños y ubicaciones. Sin contratos largos, cancela cuando quieras.`
  const url = urlF(pillar)
  // Per-pillar social card. Parking / commercial / industrial each get their
  // own; only SelfStorage still resolves to the generic boxes shot.
  const image = getPillarOgImage(pillar)

  const structuredData = createStructuredData(
    { '@type': 'Service', name: title, description, url },
    {
      provider: { '@id': 'https://www.spotme.mx' },
      serviceType: searchName,
      areaServed: DEFAULT_STRUCTURED_DATA.areaServed as unknown as string,
      audience: { '@type': 'Audience', audienceType: 'person' },
      category: label,
      ...(priceRange && {
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: priceRange.low,
          highPrice: priceRange.high,
          priceCurrency: 'MXN',
          offerCount: 500
        }
      })
    }
  )

  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const ITEMS = t('FAQS.ITEMS', { returnObjects: true })

  // Append the on-demand FAQ entry to FAQPageJsonLd whenever the rendered
  // FaqsSection appends it — keeps the schema and the rendered page in sync
  // so Google's FAQ rich-result eligibility tracks the visible content. Helper
  // returns null when the surface should not render (non-SelfStorage pillar,
  // pillar root with zero coverage, or city that doesn't resolve to a partner-
  // active market).
  const onDemandHref =
    resolveOnDemandLandingUrl(pillar, initialCity ?? undefined) ??
    '/renta-mini-bodegas/almacenamiento-a-domicilio'
  const onDemandFaqEntry = buildOnDemandFaqEntry(
    pillar,
    initialCity ?? undefined,
    {
      title: t('FAQ_ON_DEMAND.title'),
      description: t('FAQ_ON_DEMAND.description', { href: onDemandHref })
    }
  )
  const FAQ_JSONLD_ENTRIES = [
    ...ITEMS.map(({ title, description }) => ({
      questionName: title,
      acceptedAnswerText: description
    })),
    ...(onDemandFaqEntry
      ? [
          {
            questionName: onDemandFaqEntry.title,
            acceptedAnswerText: onDemandFaqEntry.descriptionPlain
          }
        ]
      : [])
  ]

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        languageAlternates={[
          { hrefLang: 'es', href: url },
          { hrefLang: 'x-default', href: url }
          // No `en` alternate: the entire /en tree is `noindex, follow` via
          // _app.tsx DefaultSeo, so pointing hreflang at a non-canonical EN
          // version causes "hreflang to non-canonical" errors in Search Console.
          // Restore the `en` entry only when EN becomes a real indexable
          // alternate with a self-referencing canonical.
        ]}
        openGraph={{
          type: 'website',
          url,
          title,
          description,
          locale: toOgLocale(locale),
          images: [
            {
              url: image,
              width: OG_IMAGE_WIDTH,
              height: OG_IMAGE_HEIGHT,
              alt: t('OG_IMAGE_ALT')
            }
          ]
        }}
        additionalMetaTags={[
          { name: 'keywords', content: keywords.join(', ') }
        ]}
      />
      <Script
        id="structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <FAQPageJsonLd mainEntity={FAQ_JSONLD_ENTRIES} />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Inicio', item: 'https://www.spotme.mx' },
          { position: 2, name: searchName, item: url }
        ]}
      />
      {pillar === PillarPage.Industrial && (
        <Script
          id="local-business-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'LocalBusiness',
              name: 'SpotMe - Naves Industriales en Renta',
              description:
                'Marketplace de naves industriales verificadas para nearshoring, manufactura y logística en México.',
              url: 'https://www.spotme.mx/renta-naves-industriales',
              logo: 'https://www.spotme.mx/logos/favicon.ico',
              // Industrial card, not the generic boxes shot — this block only
              // renders on the naves-industriales pillar.
              image: getPillarOgImage(PillarPage.Industrial),
              telephone: '+52-81-1821-6319',
              email: 'contacto@spotme.mx',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Av. Manuel Gómez Morín 350-PB 06A',
                addressLocality: 'San Pedro Garza García',
                addressRegion: 'N.L.',
                postalCode: '66265',
                addressCountry: 'MX'
              },
              geo: {
                '@type': 'GeoCoordinates',
                latitude: 25.6514,
                longitude: -100.3491
              },
              openingHoursSpecification: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: [
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday'
                ],
                opens: '09:00',
                closes: '18:00'
              },
              sameAs: [
                'https://www.facebook.com/spotmemx',
                'https://www.instagram.com/spotme.mx',
                'https://www.linkedin.com/company/spotmemx'
              ],
              priceRange: '$25,000 - $500,000+ MXN/mes'
            })
          }}
        />
      )}

      <main>
        <LandingNav />
        <PersonalizedLanding
          pillar={pillar}
          firstBlogPost={firstBlogPost}
          initialCity={initialCity}
        />
        <Footer />
        {/* revealAt matches PersonalizedLanding's STICKY_CTA_THRESHOLD_PX so
            both UI elements fade in together on mobile scroll. */}
        <WhatsAppButton
          mobileBottom="bottom-[88px]"
          revealAt={150}
          msg={t('FAQS.WHATSAPP_MSG')}
        />
        <GooglePopup />
      </main>
    </>
  )
}

/**
 * SSR conversion (April 2026): the pillar landing pages used to be statically
 * generated via `getStaticProps` + `getStaticPaths`, but we now SSR them so
 * the `?city=<slug>` URL param can be read server-side. This eliminates the
 * client-side flash on paid traffic where the personalized hero used to swap
 * after first paint.
 *
 * Performance is preserved by:
 *  1. Aggressive edge caching for organic visits (no `?city=` param). Vercel
 *     CDN serves `s-maxage=3600 stale-while-revalidate=86400` — most organic
 *     hits are still CDN-served.
 *  2. Per-city cache keys for paid visits. Visiting `?city=monterrey` caches
 *     the Monterrey-personalized HTML at the edge for `s-maxage=600`. Same
 *     city + UTM combo serves from cache.
 *  3. Skipping the Hygraph blog fetch entirely for paid traffic — `BlogSection`
 *     is hidden on the paid LP, so the network round-trip is wasteful.
 *  4. Running translations + blog (when needed) in parallel.
 */
export const getServerSideProps: GetServerSideProps<IProps> = async (
  context
) => {
  const { params, locale = 'es', query, res } = context
  const pillarParam = (params?.pillar as string | undefined) ?? ''

  if (!VALID_PILLARS.has(pillarParam)) {
    return { notFound: true }
  }
  const pillar = pillarParam as PillarPage

  const initialCity = parseInitialCity(query.city)

  // Cache headers — keep most traffic served from edge cache.
  // Organic (no city param): 6h fresh, 7d stale-while-revalidate.
  // Paid (city param present): 1h fresh, 24h stale — shorter TTL because
  // the listings carousel data is fresher on this branch and we vary by
  // query string anyway.
  //
  // Widened 2026-08-27: `stale-while-revalidate` is what actually keeps a
  // visitor off the ~300-1000 ms SSR path (past the fresh window the CDN
  // still answers instantly and revalidates behind the request), so the long
  // window is the one worth having.
  //
  // It does NOT fix ad traffic. The CDN keys on the full request URL, so a
  // unique `gclid` per click is its own cache entry and always MISSes. A
  // rewriting proxy was tried and does not help (the key is the ORIGINAL URL);
  // the only real lever left is ISR — see docs/performance/large-page-data.md.
  res.setHeader(
    'Cache-Control',
    initialCity
      ? 'public, s-maxage=3600, stale-while-revalidate=86400'
      : 'public, s-maxage=21600, stale-while-revalidate=604800'
  )

  const { searchName, label, keywords, seoTitle, seoDescription, priceRange } =
    PILLAR_PAGE_MAP[pillar]

  const translationsPromise = loadTranslations(locale, [
    'common',
    'footer',
    'nav',
    // Resolved at runtime to the active pillar's namespace. Inline (rather
    // than via a `ns` local) so the i18n linter — which scans the args list
    // for `PILLAR_NS_MAP[...]` patterns and expands them to all four pillar
    // namespaces — sees the dynamic reference directly. Matches the pattern
    // in `[city]/[hood]/index.tsx`.
    PILLAR_NS_MAP[pillar],
    'platform_stats',
    'landing',
    'auth',
    // `page_mini_bodega` intentionally NOT loaded here — the OnDemand cards
    // that use it render only on SelfStorage (where `PILLAR_NS_MAP[pillar]`
    // already IS `page_mini_bodega`) and also lazy-load it via
    // useLazyNamespace. Saves ~37 kB on every non-storage pillar page.
    // See docs/performance/large-page-data.md.
    // SpecialistCard (Fernando on Warehouse / Industrial B2B pillars)
    // uses `useTranslation('details')` for the eyebrow / subtitle / WA-CTA
    // copy. Mounted inside `<SpecialistSection />`.
    'details'
  ])

  // Skip the blog fetch entirely on paid traffic — `BlogSection` is hidden
  // there, so the round-trip is wasted latency. Saves ~50-200ms per paid
  // request.
  const blogsPromise = initialCity
    ? Promise.resolve(null)
    : getPosts(6, 0, PostOrderByInput.PublishedAtDesc, {
        locale,
        topic: PILLAR_BLOG_TOPIC[pillar] ?? undefined,
        listOnly: true
      }).catch((error) => {
        // Don't fail the page render if Hygraph is down — log and degrade.
        processHygraphRequestError(
          error,
          'Error retrieving posts from hygraph.'
        )
        return null
      })

  const [translations, blogsResult] = await Promise.all([
    translationsPromise,
    blogsPromise
  ])

  const [firstPost] = blogsResult?.data?.posts ?? []

  return {
    props: {
      ...translations,
      pillar,
      searchName,
      label,
      locale,
      firstBlogPost: (firstPost as IPost) ?? null,
      keywords,
      seoTitle,
      seoDescription,
      priceRange,
      initialCity
    }
  }
}

export default React.memo(Page)
